import { Client, IMessage, StompSubscription } from '@stomp/stompjs';
import { inject, Injectable, InjectionToken } from '@angular/core';
import SockJS from 'sockjs-client';
import { KeycloakService } from 'keycloak-angular';
import { BehaviorSubject, distinctUntilChanged, Observable, of } from 'rxjs';
import { reconnectDelayMs } from './websocket-backoff';

export interface WebSocketConfig {
  websocketUrl: string;
  /**
   * @deprecated Ignoré : la reconnexion suit un backoff exponentiel avec jitter (1 s → 30 s,
   * sans limite de tentatives), voir `reconnectDelayMs`.
   */
  reconnectDelay?: number;
  debug?: boolean;
}

export const WEBSOCKET_CONFIG = new InjectionToken<WebSocketConfig>('WEBSOCKET_CONFIG');

/**
 * État observable d'une connexion, pour que l'UI puisse signaler un temps réel coupé.
 * `disconnected` : socket fermée ; si `nextRetryAt` est renseigné (epoch ms), une tentative
 * est programmée, `attempt` en est le numéro.
 */
export interface WebSocketConnectionState {
  status: 'connecting' | 'connected' | 'disconnected';
  attempt: number;
  nextRetryAt: number | null;
}

/** Abonnement voulu par l'appelant : survit aux coupures, rejoué à chaque (re)connexion. */
interface SubscriptionIntent {
  topic: string;
  onMessage: (message: IMessage) => void;
}

export interface WebSocketConnection {
  id: string;
  client: Client;
  isConnected: BehaviorSubject<boolean>;
  state: BehaviorSubject<WebSocketConnectionState>;
  /**
   * Abonnements STOMP de la session COURANTE. Vidé à chaque fermeture de socket : stompjs
   * renumérote ses `sub-<n>` à chaque session, un ancien handle viserait un autre abonnement.
   */
  subscriptions: Map<string, StompSubscription>;
  /** Abonnements voulus, par identifiant : la source de vérité rejouée à chaque connexion. */
  intents: Map<string, SubscriptionIntent>;
  config: WebSocketConfig;
  /** Tentatives de reconnexion depuis la dernière connexion réussie. */
  reconnectAttempt: number;
}

/** Le jeton doit rester valide au moins ce temps (s) au moment d'ouvrir la session STOMP. */
const TOKEN_MIN_VALIDITY_S = 30;
/** Handshake SockJS + CONNECT non abouti dans ce délai : socket fermée, backoff relancé. */
const CONNECTION_TIMEOUT_MS = 10_000;

/** Codes de fermeture qui signalent un refus d'authentification : jeton à forcer au prochain essai. */
const AUTH_CLOSE_CODES = new Set([1008, 1011]);

interface ConnectionRuntime {
  reconnectTimer?: ReturnType<typeof setTimeout>;
  forceTokenRefresh: boolean;
  disposed: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SharedWebSocketService {
  private keycloakService = inject(KeycloakService);
  private connections = new Map<string, WebSocketConnection>();
  private runtimes = new WeakMap<WebSocketConnection, ConnectionRuntime>();
  private subscriptionSeq = 0;
  private defaultConfig: WebSocketConfig = {
    websocketUrl: '',
    debug: false,
  };

  /**
   * Create or get a websocket connection
   * @param connectionId Unique identifier for this connection
   * @param config Configuration for the websocket connection
   */
  async getConnection(connectionId: string, config: WebSocketConfig): Promise<WebSocketConnection> {
    if (this.connections.has(connectionId)) {
      return this.connections.get(connectionId)!;
    }

    const connection = await this.createConnection(connectionId, config);
    this.connections.set(connectionId, connection);
    return connection;
  }

  private async createConnection(connectionId: string, config: WebSocketConfig): Promise<WebSocketConnection> {
    const finalConfig = { ...this.defaultConfig, ...config };
    // Rejette si Keycloak ne fournit pas de jeton : l'appelant le voit (contrat inchangé).
    const token = await this.keycloakService.getToken();

    const connection: WebSocketConnection = {
      id: connectionId,
      client: null!,
      isConnected: new BehaviorSubject<boolean>(false),
      state: new BehaviorSubject<WebSocketConnectionState>({ status: 'connecting', attempt: 0, nextRetryAt: null }),
      subscriptions: new Map<string, StompSubscription>(),
      intents: new Map(),
      config: finalConfig,
      reconnectAttempt: 0,
    };
    this.runtimes.set(connection, { forceTokenRefresh: false, disposed: false });

    connection.client = this.createStompClient(connection, token);
    connection.client.activate();

    return connection;
  }

  private runtime(connection: WebSocketConnection): ConnectionRuntime {
    return this.runtimes.get(connection)!;
  }

  /**
   * Un seul client STOMP par connexion, pour toute sa durée de vie. La reconnexion automatique
   * de stompjs (délai fixe) est coupée : c'est `scheduleReconnect` qui la pilote.
   */
  private createStompClient(connection: WebSocketConnection, initialToken: string): Client {
    const client: Client = new Client({
      connectHeaders: {
        Authorization: 'Bearer ' + initialToken,
      },
      webSocketFactory: () => new SockJS(connection.config.websocketUrl),
      reconnectDelay: 0,
      connectionTimeout: CONNECTION_TIMEOUT_MS,
      beforeConnect: async () => {
        connection.state.next({ status: 'connecting', attempt: connection.reconnectAttempt, nextRetryAt: null });
        await this.refreshConnectHeaders(connection, client);
      },
      onConnect: () => {
        this.clearReconnectTimer(connection);
        connection.reconnectAttempt = 0;
        // Avant de signaler la connexion : un appelant qui se réabonne sous le même id à chaque
        // connexion (notifications) retombe alors sur l'abonnement déjà rejoué, sans doublon.
        this.replaySubscriptions(connection);
        connection.state.next({ status: 'connected', attempt: 0, nextRetryAt: null });
        connection.isConnected.next(true);
      },
      onStompError: frame => {
        // Le broker ferme la socket après un ERROR : onWebSocketClose relance la reconnexion.
        console.error('WebSocket %s : erreur STOMP', connection.id, frame.headers['message']);
        this.runtime(connection).forceTokenRefresh = true;
        connection.isConnected.next(false);
      },
      onDisconnect: () => {
        connection.isConnected.next(false);
      },
      onWebSocketError: () => {
        connection.isConnected.next(false);
      },
      onWebSocketClose: event => {
        connection.subscriptions.clear();
        connection.isConnected.next(false);
        if (event && AUTH_CLOSE_CODES.has(event.code)) {
          this.runtime(connection).forceTokenRefresh = true;
        }
        if (client.active && !this.runtime(connection).disposed) {
          this.scheduleReconnect(connection);
        } else {
          connection.state.next({ status: 'disconnected', attempt: connection.reconnectAttempt, nextRetryAt: null });
        }
      },
    });
    return client;
  }

  /**
   * Jeton frais avant chaque tentative : après une longue coupure, l'ancien a expiré. Après un
   * refus d'authentification, le rafraîchissement est forcé. Keycloak injoignable : on tente
   * avec le jeton courant, l'échec est journalisé et le backoff continue.
   */
  private async refreshConnectHeaders(connection: WebSocketConnection, client: Client): Promise<void> {
    const runtime = this.runtime(connection);
    try {
      await this.keycloakService.updateToken(runtime.forceTokenRefresh ? -1 : TOKEN_MIN_VALIDITY_S);
      runtime.forceTokenRefresh = false;
      client.connectHeaders = { Authorization: 'Bearer ' + (await this.keycloakService.getToken()) };
    } catch (error) {
      console.error('WebSocket %s : rafraîchissement du jeton impossible', connection.id, error);
    }
  }

  /** Prochaine tentative après un backoff exponentiel avec jitter, sans limite de tentatives. */
  private scheduleReconnect(connection: WebSocketConnection): void {
    this.clearReconnectTimer(connection);
    connection.reconnectAttempt++;
    const delay = reconnectDelayMs(connection.reconnectAttempt);
    connection.state.next({
      status: 'disconnected',
      attempt: connection.reconnectAttempt,
      nextRetryAt: Date.now() + delay,
    });
    this.runtime(connection).reconnectTimer = setTimeout(() => {
      this.runtime(connection).reconnectTimer = undefined;
      void this.restartClient(connection);
    }, delay);
  }

  private clearReconnectTimer(connection: WebSocketConnection): void {
    const runtime = this.runtime(connection);
    if (runtime.reconnectTimer) {
      clearTimeout(runtime.reconnectTimer);
      runtime.reconnectTimer = undefined;
    }
  }

  /** stompjs ignore `activate()` d'un client resté ACTIVE : on le désactive puis le réactive. */
  private async restartClient(connection: WebSocketConnection): Promise<void> {
    await connection.client.deactivate();
    if (this.runtime(connection).disposed) return;
    connection.client.activate();
  }

  /**
   * État de la connexion (connexion / connecté / déconnecté + prochaine tentative), pour l'UI.
   */
  connectionState(connectionId: string): Observable<WebSocketConnectionState> {
    const connection = this.connections.get(connectionId);
    if (!connection) {
      return of<WebSocketConnectionState>({ status: 'disconnected', attempt: 0, nextRetryAt: null });
    }
    return connection.state.pipe(
      distinctUntilChanged(
        (a, b) => a.status === b.status && a.attempt === b.attempt && a.nextRetryAt === b.nextRetryAt
      )
    );
  }

  /**
   * Check if a specific connection is active
   */
  isConnected(connectionId: string): Observable<boolean> {
    const connection = this.connections.get(connectionId);
    if (!connection) {
      return new BehaviorSubject<boolean>(false).asObservable();
    }
    return connection.isConnected.asObservable();
  }

  /**
   * Check if a connection is ready for communication
   */
  isConnectionReady(connectionId: string): boolean {
    const connection = this.connections.get(connectionId);
    return (connection?.isConnected.value && connection.client?.connected) || false;
  }

  /**
   * Subscribe to a topic on a specific connection. L'abonnement est rejoué automatiquement à
   * chaque reconnexion jusqu'à `unsubscribe` ; un même `subscriptionId` n'est jamais doublé.
   */
  subscribe<T = unknown>(
    connectionId: string,
    topic: string,
    callback: (message: T) => void,
    subscriptionId?: string
  ): string {
    const connection = this.connections.get(connectionId);
    if (!connection) {
      throw new Error(`Connection ${connectionId} not found. Call getConnection first.`);
    }

    const subId = subscriptionId || `${connectionId}_${topic}_${Date.now()}_${++this.subscriptionSeq}`;
    const intent: SubscriptionIntent = {
      topic,
      onMessage: message => {
        try {
          const parsedBody = JSON.parse(message.body);
          callback(parsedBody as T);
        } catch (error) {
          // If parsing fails, send raw body
          callback(message.body as T);
        }
      },
    };

    const existing = connection.intents.get(subId);
    if (existing && existing.topic === topic && connection.subscriptions.has(subId)) {
      // Déjà abonné sur cette session (ex. rejoué à la reconnexion) : on garde l'abonnement
      // serveur et on ne remplace que le callback.
      connection.intents.set(subId, intent);
      return subId;
    }
    this.dropStompSubscription(connection, subId);

    const subscription = connection.client.subscribe(topic, message =>
      connection.intents.get(subId)?.onMessage(message)
    );
    connection.intents.set(subId, intent);
    connection.subscriptions.set(subId, subscription);
    return subId;
  }

  /**
   * Unsubscribe from a topic
   */
  unsubscribe(connectionId: string, subscriptionId: string): void {
    const connection = this.connections.get(connectionId);
    if (!connection) {
      return;
    }

    connection.intents.delete(subscriptionId);
    this.dropStompSubscription(connection, subscriptionId);
  }

  /** Réémet un SUBSCRIBE pour chaque abonnement voulu, sur la session qui vient de s'ouvrir. */
  private replaySubscriptions(connection: WebSocketConnection): void {
    connection.subscriptions.clear();
    connection.intents.forEach((intent, subId) => {
      const subscription = connection.client.subscribe(intent.topic, message =>
        connection.intents.get(subId)?.onMessage(message)
      );
      connection.subscriptions.set(subId, subscription);
    });
  }

  /** Désabonne côté serveur si la session est ouverte ; un handle d'une session close est oublié. */
  private dropStompSubscription(connection: WebSocketConnection, subId: string): void {
    const subscription = connection.subscriptions.get(subId);
    connection.subscriptions.delete(subId);
    if (subscription && connection.client.connected) {
      subscription.unsubscribe();
    }
  }

  /**
   * Send a message to a destination
   */
  sendMessage(connectionId: string, destination: string, message: unknown): void {
    const connection = this.connections.get(connectionId);
    if (!connection) {
      return;
    }

    if (!this.isConnectionReady(connectionId)) {
      return;
    }

    connection.client.publish({
      destination,
      body: JSON.stringify(message),
    });
  }

  /**
   * Disconnect a specific connection
   */
  disconnect(connectionId: string): void {
    const connection = this.connections.get(connectionId);
    if (!connection) {
      return;
    }

    this.runtime(connection).disposed = true;
    this.clearReconnectTimer(connection);

    // Unsubscribe from all topics
    connection.intents.clear();
    if (connection.client.connected) {
      connection.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
    connection.subscriptions.clear();

    // Disconnect the client
    connection.client.deactivate();
    connection.isConnected.next(false);
    connection.isConnected.complete();
    connection.state.next({ status: 'disconnected', attempt: connection.reconnectAttempt, nextRetryAt: null });
    connection.state.complete();

    this.connections.delete(connectionId);
  }

  /**
   * Disconnect all connections
   */
  disconnectAll(): void {
    this.connections.forEach((_, connectionId) => {
      this.disconnect(connectionId);
    });
  }

  /**
   * Get all active connection IDs
   */
  getActiveConnections(): string[] {
    return Array.from(this.connections.keys()).filter(id => this.isConnectionReady(id));
  }

  /**
   * Reconnect a specific connection
   */
  async reconnect(connectionId: string): Promise<void> {
    const connection = this.connections.get(connectionId);
    if (connection) {
      this.clearReconnectTimer(connection);
      await connection.client.deactivate();
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (this.runtime(connection).disposed) return;
      connection.client.activate();
    }
  }

  /**
   * Refresh token and reconnect a specific connection
   */
  async refreshTokenAndReconnect(connectionId: string): Promise<void> {
    const connection = this.connections.get(connectionId);
    if (!connection) {
      return;
    }

    // Force token refresh (rejette si Keycloak échoue : l'appelant le journalise)
    await this.keycloakService.updateToken(-1);

    this.clearReconnectTimer(connection);
    connection.reconnectAttempt = 0;
    await connection.client.deactivate();
    if (this.runtime(connection).disposed) return;
    connection.client.activate();
  }

  /**
   * Refresh tokens for all active connections
   */
  async refreshAllTokensAndReconnect(): Promise<void> {
    const activeConnections = Array.from(this.connections.keys());

    if (activeConnections.length === 0) {
      return;
    }

    const refreshPromises = activeConnections.map(connectionId =>
      this.refreshTokenAndReconnect(connectionId).catch(error => {
        // Une connexion en échec n'empêche pas les autres ; l'échec reste visible.
        console.error('WebSocket %s : rafraîchissement du jeton et reconnexion impossibles', connectionId, error);
      })
    );

    await Promise.all(refreshPromises);
  }
}
